import React, { PropTypes } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import { FormattedMessage, injectIntl, FormattedHTMLMessage, intlShape } from 'react-intl';
import { getDigitalFormatTypeString } from '@bibliocommons/utils-format';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import Loader from '@bibliocommons/base-loader';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import MessageWithFallback from '@bibliocommons/localization-message-with-fallback';
import DigitalDurations from '../../digital-durations';
import '../styles/DigitalFormats.scss';

export class DigitalFormats extends BcReactComponent {
  constructor(props) {
    super(props);
    this.state = {
      selected: null
    };
    this.bindAll('renderFormat', 'handleSelect');
  }

  componentDidMount() {
    if (!this.props.formats) {
      this.props.fetchDigitalFormats(this.props.metadataId);
    }
  }

  getDigitalFormatDescription() {
    const format = this.props.formats.first().get('name');
    const formatType = getDigitalFormatTypeString(format);
    if (this.props.selectable) {
      return <FormattedMessage id={`please_pick_a_format_${formatType}`} />;
    }

    return (
      <MessageWithFallback id={`digital_content_${this.props.provider.toLowerCase()}_desc`}>
        <MessageWithFallback
          id={`${formatType}_available_now`}
          values={{
            help_link: <a href={this.props.intl.formatMessage({ id: `${formatType}_help_url` })}>
              <span aria-hidden><FormattedMessage id="learn_more" /></span>
              <ScreenReaderMessage><FormattedMessage id={`ada_${formatType}_help_text`} /></ScreenReaderMessage>
            </a>
          }}>
          <FormattedMessage id={this.props.isAvailable ? 'available_formats_now' : 'available_formats'} />
        </MessageWithFallback>
      </MessageWithFallback>
    );
  }

  /* istanbul ignore next */
  /* eslint-disable global-require */
  static getFormatImage(format) {
    switch (format.get('name').toLowerCase()) {
      case 'acoustik':
        return require('@bibliocommons/assets/images/digital_formats/acoustik.png');
      case 'adobe_epub_ebook':
        return require('@bibliocommons/assets/images/digital_formats/adobe_epub_ebook.png');
      case 'adobe_pdf_ebook':
        return require('@bibliocommons/assets/images/digital_formats/adobe_pdf_ebook.png');
      case 'biblio_ereader':
        return require('@bibliocommons/assets/images/digital_formats/biblio_ereader.png');
      case 'blio':
        return require('@bibliocommons/assets/images/digital_formats/blio.png');
      case 'kindle':
        return require('@bibliocommons/assets/images/digital_formats/kindle.png');
      case 'mmm_ebook':
        return require('@bibliocommons/assets/images/digital_formats/mmm_ebook.png');
      case 'music':
        return require('@bibliocommons/assets/images/digital_formats/music.png');
      case 'open_epub_ebook':
        return require('@bibliocommons/assets/images/digital_formats/open_epub_ebook.png');
      case 'open_pdf_ebook':
        return require('@bibliocommons/assets/images/digital_formats/open_pdf_ebook.png');
      case 'overdrive_mp3_audiobook':
        return require('@bibliocommons/assets/images/digital_formats/overdrive_mp3_audiobook.png');
      case 'overdrive_read':
        return require('@bibliocommons/assets/images/digital_formats/overdrive_read.png');
      case 'overdrive_wma_audiobook':
        return require('@bibliocommons/assets/images/digital_formats/overdrive_wma_audiobook.png');
      case 'streaming_video':
        return require('@bibliocommons/assets/images/digital_formats/streaming_video.png');
      case 'video':
        return require('@bibliocommons/assets/images/digital_formats/video.png');
      default:
        return null;
    }
  }
  /* eslint-enable global-require */

  handleSelect(format) {
    this.setState({
      selected: format
    });
    this.props.onSelectFormat(format);
  }

  renderDurations() {
    if (!this.state.selected || !this.props.selectable) {
      return null;
    }
    const durations = this.state.selected.get('durations');
    if (durations && durations.size > 1) {
      return (
        <DigitalDurations durations={durations} handleSelect={this.props.handleDurationSelect} />
      );
    }

    if (durations && durations.size === 1) {
      this.props.handleDurationSelect(durations.first());
    }

    return null;
  }

  renderFormat(format) {
    let radioButton = null;
    const formatLabel = format.get('additionalInfo') || (<FormattedHTMLMessage id={`digiformat_${format.get('name').toLowerCase()}`} />);
    const image = DigitalFormats.getFormatImage(format);
    const formatSelectable = this.props.selectable && (format.get('hasDownloadLink') || !format.get('durations').isEmpty());
    const id = `format-${format.get('name')}`;
    const classNames = cn('format-item', `format-${_.kebabCase(format.get('name'))}`, { selectable: formatSelectable, selected: this.state.selected === format });
    if (formatSelectable) {
      radioButton = (
        <input
          type="radio"
          name="format-select"
          id={id}
          className="format-radio"
          onChange={() => this.handleSelect(format)}
          required />
      );
    }
    return (
      <label htmlFor={id} className={classNames}>
        {radioButton}
        <div className="format-label" >
          <span className="format-label-name">{formatLabel}</span>
          { image && <img src={image} alt={`${formatLabel} logo`} className="format-logo" /> }
        </div>
      </label>
    );
  }

  render() {
    if (!this.props.formats) {
      return <Loader />;
    }
    if (this.props.formats.count() === 0) {
      return (<h3><FormattedMessage id="digital_format_not_available" /></h3>);
    }
    return (
      <div className="cp-digital-formats">
        <span>{this.getDigitalFormatDescription()}</span>
        <div className="format-group">
          {this.props.formats.map(this.renderFormat)}
        </div>
        {this.renderDurations()}
      </div>
    );
  }
}

DigitalFormats.displayName = 'DigitalFormats';

DigitalFormats.propTypes = {
  formats: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    hasDownloadLink: PropTypes.bool
  })).isRequired,
  isAvailable: PropTypes.bool,
  provider: PropTypes.string,
  supportsReadNow: PropTypes.bool,
  titleAvailableForReader: PropTypes.bool,
  selectable: PropTypes.bool,
  onSelectFormat: PropTypes.func,
  intl: intlShape,
  handleDurationSelect: PropTypes.func,
  fetchDigitalFormats: PropTypes.func.isRequired,
  metadataId: PropTypes.string.isRequired
};

export default injectIntl(DigitalFormats);



// WEBPACK FOOTER //
// ./app/components/shared/ItemTransactions/components/digital-formats/components/DigitalFormats.component.jsx